/**
 * Computes occupancy over time from raw rows:
 * occupancy[t] = cumulative sum of (in - out) per time bucket (start).
 *
 * flowRows: [{ start, in, out, uuid } ...] (uuid can be in _id.uuid)
 * allowedUuids: optional Set of uuids to include (filter)
 *
 * Returns: { starts, labels, occupancy, endOccupancy, sumIn, sumOut }
 */
export function computeOccupancyFromFlowRows(flowRows, allowedUuids, { zeroEnd = false, clamp0 = true } = {}) {
  const buckets = new Map(); // start -> { in, out }

  for (const r of flowRows || []) {
    const uuid = r.uuid || r?._id?.uuid;
    if (!uuid) continue;
    if (allowedUuids && !allowedUuids.has(uuid)) continue;

    const start = Number(r.start);
    if (!Number.isFinite(start)) continue;

    // IMPORTANT: out must be POSITIVE in data (do not pass negative egress rows)
    const inVal = Number(r.in ?? 0);
    const outVal = Number(r.out ?? 0);

    if (!buckets.has(start)) buckets.set(start, { in: 0, out: 0 });
    const b = buckets.get(start);
    b.in += inVal;
    b.out += outVal;
  }

  const starts = Array.from(buckets.keys()).sort((a, b) => a - b);

  const labels = starts.map((s) => {
    return s * 1000;
  });

  let current = 0;
  const occupancy = starts.map((s) => {
    const b = buckets.get(s);
    current += (b.in - b.out);
    return clamp0 ? Math.max(0, current) : current;
  });

  const sumIn = starts.reduce((acc, s) => acc + buckets.get(s).in, 0);
  const sumOut = starts.reduce((acc, s) => acc + buckets.get(s).out, 0);

  if (zeroEnd && occupancy.length) {
    const offset = occupancy[occupancy.length - 1];
    for (let i = 0; i < occupancy.length; i++) occupancy[i] -= offset;
    if (clamp0) for (let i = 0; i < occupancy.length; i++) occupancy[i] = Math.max(0, occupancy[i]);
  }

  return {
    starts,
    labels,
    occupancy,
    endOccupancy: occupancy.length ? occupancy[occupancy.length - 1] : 0,
    sumIn,
    sumOut
  };
}
